import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'

import { trackEvent } from './apiAnalytics'
import { useOrgUsersApi } from './apiOrgUsers'
import { queryKeys } from './queryKeys'

export type InsertOrganization =
  Database['public']['Tables']['organization']['Insert']
export type UpdateOrganization =
  Database['public']['Tables']['organization']['Update']
export type Organization_ = Database['public']['Tables']['organization']['Row']

export interface PaginatedOrgsParams {
  page: number
  pageSize: number
  source?: 'internal' | 'external'
  /** Exclude placeholder org name "Anon" from results */
  excludeAnon?: boolean
  search?: string
  sortBy?: string
  sortDesc?: boolean
}

export interface SearchOrgsParams {
  search: string
  source?: 'internal' | 'external'
  limit?: number
  enabled?: boolean
  /** Exclude placeholder org name "Anon" from results */
  excludeAnon?: boolean
  /** Exclude rows where name is null or empty string */
  excludeEmptyName?: boolean
}

export const useOrgsApi = ({
  org_id,
  fetchOrganizations = false,
}: { org_id?: string; fetchOrganizations?: boolean } = {}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { orgUser } = useOrgUsersApi()

  const effectiveOrgId = orgUser?.data?.organization_id ?? undefined

  const { data: organization } = useQuery({
    queryKey: [queryKeys.organization, effectiveOrgId],
    queryFn: async () =>
      await supabase
        .from('organization')
        .select('*')
        .filter('id', 'eq', effectiveOrgId!)
        .single(),
    enabled: !!effectiveOrgId,
  })

  const { data: organizationById } = useQuery({
    queryKey: [queryKeys.organization, org_id],
    queryFn: async () =>
      await supabase
        .from('organization')
        .select('*')
        .eq('id', org_id!)
        .single(),
    enabled: !!org_id,
  })

  const { data: organizations } = useQuery({
    queryKey: [queryKeys.organizations],
    queryFn: async () => await supabase.from('organization').select('*'),
    enabled: fetchOrganizations,
  })

  const usePaginatedOrganizations = ({
    page,
    pageSize,
    source,
    excludeAnon = false,
    search,
    sortBy = 'created_at',
    sortDesc = true,
  }: PaginatedOrgsParams) => {
    return useQuery({
      queryKey: [
        queryKeys.organizations,
        'paginated',
        page,
        pageSize,
        source,
        excludeAnon,
        search,
        sortBy,
        sortDesc,
      ],
      queryFn: async () => {
        const from = page * pageSize
        const to = from + pageSize - 1

        let query = supabase
          .from('organization')
          .select('*', { count: 'exact' })
          .order(sortBy, { ascending: !sortDesc })
          .range(from, to)

        if (source) {
          query = query.eq('source', source)
        }

        if (excludeAnon) {
          query = query.neq('name', 'Anon')
        }

        if (search) {
          query = query.ilike('name', `%${search}%`)
        }

        return await query
      },
    })
  }

  const useSearchOrganizations = ({
    search,
    source,
    limit = 20,
    enabled = true,
    excludeAnon = false,
    excludeEmptyName = false,
  }: SearchOrgsParams) => {
    return useQuery({
      queryKey: [
        queryKeys.organizations,
        'search',
        search,
        source,
        limit,
        enabled,
        excludeAnon,
        excludeEmptyName,
      ],
      queryFn: async () => {
        let query = supabase
          .from('organization')
          .select('*')
          .order('name', { ascending: true })
          .limit(limit)

        if (search) {
          query = query.ilike('name', `%${search}%`)
        }

        if (source) {
          query = query.eq('source', source)
        }

        if (excludeAnon) {
          query = query.neq('name', 'Anon')
        }

        if (excludeEmptyName) {
          query = query.not('name', 'is', null).neq('name', '')
        }

        return await query
      },
      enabled,
    })
  }

  const createOrganization = useMutation({
    mutationFn: async (data: InsertOrganization) =>
      await supabase.from('organization').insert(data).select('id'),
    onSuccess: (result, data) => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organizations] })
      const orgId = result.data?.[0]?.id
      if (orgId && data.source === 'internal') {
        trackEvent(supabase, {
          name: 'org_added',
          orgId,
          onceKey: `org_added:${orgId}`,
        })
      }
    },
  })

  const updateOrganization = useMutation({
    mutationFn: async (updateData: UpdateOrganization) =>
      await supabase
        .from('organization')
        .update(updateData)
        .eq('id', updateData.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organization] })

      toast({
        variant: 'success',
        title: 'Updated organization',
      })
    },
  })

  const deleteOrganization = useMutation({
    mutationFn: async (id: string) =>
      await supabase.from('organization').delete().eq('id', id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.organizations] })

      toast({
        variant: 'success',
        title: 'Organization deleted',
      })
    },
  })

  const createOrganization2 = useMutation({
    mutationFn: async (data: InsertOrganization) => {
      const organization = await supabase
        .from('organization')
        .select('*')
        .eq('company_url', data.company_url!)
        .maybeSingle()

      if (organization.data) {
        return organization
      } else {
        // Creates the org and the caller's org_user membership in one step
        // (RLS only lets staff insert organization / org_user rows directly).
        const { data: orgId, error } = await supabase.rpc(
          'create_own_organization',
          {
            p_name: data.name,
            p_company_url: data.company_url ?? undefined,
            p_logo_urls: data.logo_urls ?? undefined,
            p_chosen_logo_url: data.chosen_logo_url ?? undefined,
            p_source: data.source ?? undefined,
          }
        )
        return { data: orgId ? { id: orgId } : null, error }
      }
    },
  })

  return {
    organization,
    organizationById,
    organizations,
    usePaginatedOrganizations,
    useSearchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    createOrganization2,
  }
}
